import React, { useCallback, useEffect, useState } from "react";
import {
  makeStyles,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Box,
  Select,
  MenuItem,
} from "@material-ui/core";
import ReactLoadingOverlay from "react-loading-overlay";
import ClipLoader from "react-spinners/ClipLoader";
import LocationDetails from "./LocationDetails";
import Search_Icon from "../../assets/images/Search_Icon.svg";
import Bulk_Upload from "../../assets/images/Bulk_Upload.svg";
import {
  FnAuthenticatedRequest,
  handleSearchValidation,
  hasRole,
  SERVICES
} from "../../Utility/Utils";
import { Pagination } from "@material-ui/lab";
import Menu from "./Menu";
import {
  allowedRoles,
  userTablePageNumber,
  userTablePageSize,
} from "../../Utility/constants";
import StorageHelper from "../../Utility/StorageHelper";
import { useHistory } from "react-router-dom";
import { APP_ROUTES } from "../../Libraries/AppConstants";
import BulkUploads from "./BulkUploads";
import { toast } from "react-toastify";


const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    overflowX: "auto",
  },
  table: {
    minWidth: 650,
    border: "none",
    [theme.breakpoints.down("sm")]: {
      overflowX: "scroll",
    },
  },
  tableContainer: {
    borderSpacing: "15px",
  },
  tableRow: {
    borderBottom: "none",
  },
  bold: {
    fontWeight: "bold",
  },
  editIcon: {
    color: "gray",
  },
}));

const Kyc: React.FC = () => {
  const classes = useStyles();
  const [users, setUsers] = useState<any>([]);
  const [open, setOpen] = React.useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(userTablePageSize);
  const [userCount, setUserCount] = useState(0);
  const [districtParams, setDistrictParams] = useState(null);
  const [talukaParams, setTalukaParams] = useState(null);
  const [villageParams, setVillageParams] = useState(null);
  const [projectParams, setProjectParams] = useState(null);
  const [statesParams, setStatesParams] = useState(null);
  const [territoryParams, setTerritoryParams] = useState(null);
  const [filteredFarmers, setFilteredFarmers] = useState<any>([]);
  const [prevInput, setPrevInput] = useState("");
  const [apiCalled, setApiCalled] = useState<boolean>(false);
  const [bulkUpload, setBulkUpload] = useState<boolean>(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [kycOverrideStates, setKycOverrideStates] = useState<any>({});
  const handleClickOpenBulkUpload = (bulkUpload) => {
    setOpen(true);
    setBulkUpload(bulkUpload);
    setSearchInput("");
    setPageNumber(0);
  };
  const handleClose = () => {
    setOpen(false);
    setBulkUpload(false);
    handleSetMetaParams(null, null, null, null, null, null, setSearchInput);
  };

  function showLast8(id) {
    if (!id) return "";
    return id.slice(-8);
  }

  const history = useHistory();

  const handleGetAllFarmers: any = useCallback(
    async (params) => {
      try {
        setDataLoading(true);
        const authData = JSON.parse(localStorage.getItem("auth") || "{}");
        if (authData) {
          const authHeader = {
            JWTToken: authData.jwtToken,
            userId: authData.userId,
          };
          const payload = {
            stateIds: params?.states,
            districtIds: params?.districts,
            territoryIds: params?.territories,
            projectIds: params?.project,
            talukaIds: params?.talukas,
            villageIds: params?.village,
            searchText: params?.searchInput || "",
            pageSize: params?.pageSize,
            page: params?.pageNo + 1,
          };
          const response: any = await FnAuthenticatedRequest(
            "POST",
            "KycOverride/KycPendingFarmers",
            authHeader,
            payload,
            SERVICES.KYC
          );

          if (response?.success) {
            setDataLoading(false);
            setUsers(response?.data?.results);
            setFilteredFarmers(response?.data?.results);
            setUserCount(response?.data?.totalRecords || 0);
            return response?.data?.results;
          } else {
            setDataLoading(false);
            setFilteredFarmers([]);
            setUserCount(0);
          }
        }
      } catch (error: any) {
        setDataLoading(false);
        if (error.response && error.response.status) {
          const statusCode = error.response.status;
          if (statusCode === 401) {
            StorageHelper.removeAll();
            document.cookie.split(";").forEach(function (c) {
              document.cookie = c
                .replace(/^ +/, "")
                .replace(
                  /=.*/,
                  "=;expires=" + new Date().toUTCString() + ";path=/"
                );
            });

            history.replace(APP_ROUTES.LOGIN);
          } else {
            console.error(error);
          }
        } else {
          console.error(error);
        }
      }
      // eslint-disable-next-line
    },
    [history]
  );

  const normalizeKycStatus = (status?: string | null) => {
    if (!status) return null;

    const value = status.toLowerCase().replace(/\s+/g, " ").trim();

    if (value === "open") return "Open";
    if (value === "not open") return "Not Open";

    return null; // fallback for unexpected values
  };

  const handleSearchInput = async (users, input) => {
    try {
      setPrevInput(input);
      setSearchInput(input);

      if (input?.length > 3 && input !== prevInput) {
        const params = {
          pageNo: 0,
          pageSize: userTablePageSize,
          states: statesParams,
          territories: territoryParams,
          districts: districtParams,
          project: projectParams,
          talukas: talukaParams,
          village: villageParams,
          searchInput: input,
        };

        setApiCalled(true);
        await handleGetAllFarmers(params);
      } else if (input?.length <= 3 && prevInput !== null && apiCalled) {
        const params = {
          pageNo: 0,
          pageSize: userTablePageSize,
          states: statesParams,
          territories: territoryParams,
          districts: districtParams,
          project: projectParams,
          talukas: talukaParams,
          village: villageParams,
          searchInput: null,
        };
        setPageNumber(0);

        setApiCalled(false);
        await handleGetAllFarmers(params);
      }
    } catch (error) {
      console.error(error);
    }
  };


  useEffect(() => {
    if (!open) {
      const fetchData = async () => {
        try {
          const params = {
            pageNo: pageNumber,
            pageSize: pageSize,
            states: null,
            territories: null,
            districts: null,
            project: null,
            talukas: null,
            village: null,
            searchInput: null,
          };
          const users = await handleGetAllFarmers(params);
          if (users) {
            setUsers(users);
            if (searchInput) {
              handleSearchInput(users, searchInput);
            }

          }
        } catch (error) {
          console.error("Error in getAllKa:", error);
        }
      };
      fetchData();
    }
  }, [open]);

  useEffect(() => {
    if (searchInput) {
      handleSearchInput(users, searchInput);
    }
  }, [searchInput, users]);

  const handleResetPageNumber = () => {
    try {
      setPageNumber(userTablePageNumber);
      setPageSize(userTablePageSize);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSetMetaParams: any = (
    projectParams,
    stateParams,
    territoryParams,
    disctrictParams,
    talukaParams,
    villageParams,
    searchInput
  ) => {
    try {
      setProjectParams(projectParams);
      setStatesParams(stateParams);
      setTerritoryParams(territoryParams);
      setDistrictParams(disctrictParams);
      setTalukaParams(talukaParams);
      setVillageParams(villageParams);
      setSearchInput(searchInput);
    } catch (error) {
      console.error(error);
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPageNumber(value - 1);
    const input = searchInput?.length > 3 ? searchInput : "";
    const params = {
      pageNo: value - 1,
      pageSize: pageSize,
      project: projectParams,
      states: statesParams,
      territories: territoryParams,
      districts: districtParams,
      talukas: talukaParams,
      village: villageParams,
      searchInput: input,
    };
    handleGetAllFarmers(params);
  };

  // Initialize KYC override states for each farmer
  const initializeKycStates = (farmersList: any[]) => {
    const newStates: any = {};
    farmersList.forEach((farmer: any) => {
      if (!kycOverrideStates[farmer?.farmerId]) {
        newStates[farmer?.farmerId] = {
          isKycOverride: farmer?.isKycOverride || false,
          kycStatus: farmer?.isKycOverride ? farmer?.kycStatus ? "Open" : "Not Open" : "",

        };
      }
    });
    if (Object.keys(newStates).length > 0) {
      setKycOverrideStates((prev: any) => ({ ...prev, ...newStates }));
    }
  };

  // Initialize KYC states when users load
  useEffect(() => {
    if (filteredFarmers?.length > 0) {
      initializeKycStates(filteredFarmers);
    }
  }, [filteredFarmers]);

  // Handle Override KYC dropdown change
  const handleOverrideKycChange = (farmerId: string, value: string) => {
    const isOverride = value === "true";
    setKycOverrideStates((prev: any) => ({
      ...prev,
      [farmerId]: {
        isKycOverride: isOverride,
        kycStatus: isOverride ? "Open" : "",
      },
    }));
    const kycStatus = isOverride ? "Open" : "";
    handleUpdateKycOverride(farmerId, isOverride, kycStatus);
  };

  // Handle KYC Status dropdown change
  const handleKycStatusChange = (farmerId: string, value: string) => {
    setKycOverrideStates((prev: any) => ({
      ...prev,
      [farmerId]: {
        ...prev[farmerId],
        kycStatus: value,
        isKycOverride: true
      },
    }));

    const isKycOverrideValue = true;
    handleUpdateKycOverride(farmerId, isKycOverrideValue, value);
  };

  // API call handler for KYC Override update
  const handleUpdateKycOverride = async (
    farmerId: string,
    isKycOverride: boolean,
    kycStatus: string
  ) => {
    try {
      const authData = JSON.parse(localStorage.getItem("auth") || "{}");
      if (authData) {
        const authHeader = {
          JWTToken: authData.jwtToken,
          userId: authData.userId,
        };

        // Build payload - only send isKycOverride
        const payload = {
          farmerId: farmerId,
          isKycOverrideEnabled: isKycOverride,
          isKycStatusEnable: isKycOverride ? kycStatus == "Open" : false,
        };
        const response: any = await FnAuthenticatedRequest(
          "POST",
          "KycOverride/KycOverride",
          authHeader,
          payload,
          SERVICES.KYC
        );
        if (response?.success) {
          toast.success(response?.data || "Updated Successfully", {
            position: toast.POSITION.TOP_CENTER,
            style: {
              backgroundColor: "#258750",
              color: "white",
            },
            autoClose: 3000,
          });

        }
      }


    } catch (error: any) {
      setDataLoading(false);
      if (error.response && error.response.status) {
        const statusCode = error.response.status;
        if (statusCode === 401) {
          StorageHelper.removeAll();
          document.cookie.split(";").forEach(function (c) {
            document.cookie = c
              .replace(/^ +/, "")
              .replace(
                /=.*/,
                "=;expires=" + new Date().toUTCString() + ";path=/"
              );
          });

          history.replace(APP_ROUTES.LOGIN);
        } else {
          console.error(error);
        }
      } else {
        console.error(error);
      }
    }
  };

  return (
    <div className="container min-w-full">
      {!open ? (
        <>
          <div className="flex px-5 py-3">
            <h1 className="text-xl md:text-3xl font-bold">KYC Override (only KYC incomplete Farmers)</h1>
            <div className="ml-auto flex gap-3">
              {/* <button className="bg-white md:p-2 md:px-3 rounded text-[#009CA7] border-2 border-[#009CA7] transition-all hover:bg-[#e0f2f1] hover:text-[#007a80] active:bg-[#b2dfdb] active:border-[#b2dfdb]"> */}
              {
                hasRole(allowedRoles) &&
                <button onClick={() => handleClickOpenBulkUpload(true)}>
                  {/* <FileDownloadOutlinedIcon /> */}
                  <img src={Bulk_Upload} alt="bulk_upload" />
                </button>
              }

            </div>
          </div>
          <hr className="my-1 border-[1.5px]" />
          <div className="min-w-full">
            <div className="container px-5 py-2 min-w-full">
              <LocationDetails
                fnGetAllFarmers={handleGetAllFarmers}
                handleResetPageNumber={handleResetPageNumber}
                setSearchInput={searchInput}
                handleSetMetaParams={handleSetMetaParams}
              />
            </div>

            <div className="m-3 mt-6 border-2 border-gray-300 bg-[#E5F5F6]">
              <div className="block sm:flex bg-[#E5F5F6]">
                <div className="search-container my-3 mx-2">
                  <input
                    type="text"
                    placeholder="Search by Farmer Id, Number"
                    style={{ fontSize: "12px" }}
                    className="search-input min-w-[100%] sm:min-w-[125%]"
                    onKeyPress={handleSearchValidation}
                    value={searchInput}
                    onChange={(e: any) => {
                      handleSearchInput(users, e.target.value);
                    }}
                  />
                  <div className="search-icon">
                    <img src={Search_Icon} alt="Search_Icon" />
                  </div>
                </div>
                <div className="flex ml-auto gap-3 items-center px-3 py-2">
                  <p className="text-[#009CA7] text-sm pb-2 ml-auto font-bold">
                    Count - {userCount}
                  </p>
                </div>
              </div>

              <hr className="border border-gray-200" />

              <TableContainer component={Paper}>
                <Table className={classes.table} aria-label="simple table">
                  <TableHead>
                    <TableRow className="bg-[#E5F5F6] text-nowrap">
                      <TableCell className={classes.bold}>Farmer ID</TableCell>
                      <TableCell className={classes.bold}>Farmer Name</TableCell>
                      <TableCell className={classes.bold}>Farmer Number</TableCell>
                      {/* <TableCell className={classes.bold}>State/District/Block/Village</TableCell> */}
                      <TableCell className={classes.bold}>State</TableCell>
                      <TableCell className={classes.bold}>District</TableCell>
                      <TableCell className={classes.bold}>Block</TableCell>
                      <TableCell className={classes.bold}>
                        Village
                      </TableCell>
                      <TableCell className={classes.bold}>Tagged Area (acres)</TableCell>
                      <TableCell className={classes.bold}>Eligibility Status</TableCell>
                      <TableCell className={classes.bold}>Override KYC</TableCell>
                      <TableCell className={classes.bold}>KYC Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <>
                    {!dataLoading ? (
                      <>
                        <TableBody>
                          {filteredFarmers?.length > 0 ? (
                            filteredFarmers?.map((farmer: any, index) => (
                              <TableRow
                                key={index}
                                className="border-0 text-nowrap"
                              >
                                <TableCell>{showLast8(farmer?.farmerId)}</TableCell>
                                <TableCell>{farmer?.farmerName}</TableCell>
                                <TableCell>{farmer?.mobile}</TableCell>
                                <TableCell>{farmer?.state}</TableCell>
                                <TableCell>{farmer?.district}</TableCell>
                                <TableCell>{farmer?.taluka}</TableCell>
                                <TableCell>{farmer?.village}</TableCell>
                               <TableCell>
  {Number(farmer?.tagedArea) === 0 ? "0.0" : farmer?.tagedArea}
</TableCell>
                                <TableCell>{farmer?.isEligible ? "Eligible" : "Not Eligible"}</TableCell>
                                <TableCell>
                                  <Select
                                    variant="outlined"
                                    className="no-hover-bg"
                                    disabled={!hasRole(allowedRoles)}
                                    value={kycOverrideStates[farmer?.farmerId]?.isKycOverride ? "true" : "false"}
                                    onChange={(e: any) => handleOverrideKycChange(farmer?.farmerId, e.target.value)}
                                    style={{ width: "100%", fontSize: "12px", padding: "6px 12px", height: "32px", backgroundColor: "white" }}
                                  >
                                    <MenuItem value="true">Yes</MenuItem>
                                    <MenuItem value="false">No</MenuItem>

                                  </Select>
                                </TableCell>
                                <TableCell width={"155px"}>
                                  <Select
                                    variant="outlined"
                                    className="no-hover-bg"
                                    value={kycOverrideStates[farmer?.farmerId]?.kycStatus || ""}
                                    onChange={(e: any) => handleKycStatusChange(farmer?.farmerId, e.target.value)}
                                    disabled={!kycOverrideStates[farmer?.farmerId]?.isKycOverride || !hasRole(allowedRoles)}
                                    style={{ width: "100%", fontSize: "12px", padding: "6px 12px", height: "32px" }}
                                  >
                                    {/* <MenuItem value="">--Select--</MenuItem> */}
                                    <MenuItem value="Open">Open</MenuItem>
                                    <MenuItem value="Not Open">Not Open</MenuItem>
                                  </Select>
                                </TableCell>



                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={17} className="text-center">
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    minHeight: "300px",
                                  }}
                                >
                                  <p>No Records Found</p>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </>
                    ) : (
                      <>
                        <ReactLoadingOverlay
                          classNamePrefix="growindigo_"
                          active={true}
                          spinner={<ClipLoader color="#27878E" />}
                          text={""}
                        >
                          {/* Your content here */}
                        </ReactLoadingOverlay>
                      </>
                    )}
                  </>
                </Table>
              </TableContainer>
              <div className="flex flex-col bg-[#F4F4F7] md:flex md:flex-row items-center justify-center py-5">
                <Pagination

                  count={
                    users?.length > 0
                      ? filteredFarmers &&
                      Math.ceil(userCount / pageSize)
                      : 2
                  }
                  disabled={filteredFarmers?.length === 0 ? true : false}
                  page={pageNumber + 1}
                  shape="rounded"
                  onChange={handlePageChange}
                />
                <Menu
                  pageSize={pageSize}
                  changeDefaultTotal={(number) => {
                    const params = {
                      pageNo: 0,
                      pageSize: number,
                      project: projectParams,
                      states: statesParams,
                      territories: territoryParams,
                      districts: districtParams,
                      talukas: talukaParams,
                      village: villageParams,
                      searchInput: searchInput,
                    };
                    handleGetAllFarmers(params);
                    setPageSize(number);
                    setPageNumber(0);
                  }}
                  perPage={[10, 15, 25]}
                />
              </div>
            </div>
          </div>
        </>
      ) : open === true && bulkUpload === true ? (
        <BulkUploads
          handleBack={handleClose}
          bulkUpload={bulkUpload}
          users={users}
        />
      ) : null}
    </div>
  );
};

export default Kyc;
